import { format } from 'date-fns'
import { useRouter } from 'next/router'
import React, { useState, useEffect } from 'react'

import Head from 'next/head'

let styles = {}
import stylesA from '@/styles/Post.module.css'
import stylesB from '@/styles/index.module.css'

import Header from '@/components/Header'
import BreadCrumb from '@/components/ui/breadcrumb/BreadcrumbComponent'
import ConnectContainer from '@/components/ConnectContainer'
import FooterContainer from '@/components/FooterContainer'
import Link from 'next/link'

import BlockContentRenderer from '@/utils/BlockContentRenderer'

import mockPost from '@/data/mockPost'
import { getPost } from '@/studio/utils/sanity-utils'

import { parseOutline } from '@/utils/HeadingUtils'

import TableOfContents from '@/components/TableOfContents'
import SocialShareButtons from '@/components/social/SocialShareButtons'
import RelevantConcepts from '@/components/content/RelevantConcepts'
import { toZonedTime } from 'date-fns-tz'
import Footer from '@/components/home/v2/Footer'

Object.assign(styles, stylesA, stylesB)

//const Post = () => {
export default function Post({ post }) {
  if (!post) {
    // You can show an error message, a loading indicator, or return null
    return <div>Post not found</div>
  }
  const [headingData, setHeadingData] = useState([])

  const [sectionRefs, setSectionRefs] = useState({})

  // This runs after the component mounts and every time `page` changes.
  useEffect(() => {
    if (post && post.body) {
      const newHeadingData = parseOutline(post.body)
      setHeadingData(newHeadingData)

      // Create a ref for each heading
      const newSectionRefs = newHeadingData.reduce((refs, heading) => {
        refs[heading.slug] = React.createRef()
        return refs
      }, {})

      setSectionRefs(newSectionRefs)
    }
  }, [post])

  const { title, publishedAt, image, description, body } = post

  const router = useRouter()
  const slug = router.query.slug

  let location = `${slug}`

  const url = `https://clearwatt.co.uk/blog/${slug}`

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const zonedDate = toZonedTime(date, 'Etc/UTC')
    return format(zonedDate, 'MMMM dd, yyyy')
  }

  return (
    <>
      <Head>
        <title>{`${title} - ClearWatt Blog`}</title>
        <meta
          name="description"
          content={
            description ||
            `Read the latest insights and updates on ${title} from ClearWatt's EV-focused blog. Stay informed about the electric vehicle revolution.`
          }
        />
        <meta name="viewport" content="maximum-scale=1, initial-scale=1, width=device-width" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={
            description ||
            `Learn about ${title} in ClearWatt's EV-focused blog. Your source for updates and insights on the electric vehicle market.`
          }
        />
        <meta property="og:image" content={image} />
        <meta property="og:article:published_time" content={publishedAt} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={url} />
        <meta property="twitter:title" content={title} />
        <meta
          property="twitter:description"
          content={description || `Explore ${title} on ClearWatt's blog. Stay informed about EV trends and news.`}
        />
        <meta property="twitter:image" content={image} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: title,
            description: description,
            image: image,
            datePublished: publishedAt,
            author: {
              '@type': 'Organization',
              name: 'ClearWatt',
            },
            publisher: {
              '@type': 'Organization',
              name: 'ClearWatt',
              logo: {
                '@type': 'ImageObject',
                url: 'https://clearwatt.co.uk/logo.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': url,
            },
          })}
        </script>

        <link rel="icon" href="/favicon.png" />
        <link rel="canonical" href={url} />
      </Head>

      <Header />
      <div className={styles.post}>
        <div className={styles.postContainer}>
          <BreadCrumb location={title} />

          <div className={styles.postHeader}>
            <div className={styles.postInfo}>
              <div className={styles.postDate}>{formatDate(publishedAt)}</div>
              <h1 className={styles.postTitle}>{title}</h1>
              <p className={styles.postDesc}>{description}</p>
            </div>
            <div className={styles.socialContainer}>
              <div className={styles.shareLabel}>Share</div>
              <SocialShareButtons url={url} title={title} image={image} description={description} />
            </div>
          </div>

          {image && <img src={image} alt={title} className={styles.postImage} />}

          <div className={styles.postContent}>
            <div className={styles.postContentSidebarWrapper}>
              <div className={styles.postContentSidebar}>
                <div className={styles.tableTitle}>Table of contents</div>

                <TableOfContents headingData={headingData} sectionRefs={sectionRefs} />
              </div>
            </div>

            <div className={styles.postContentMain}>
              <BlockContentRenderer
                blocks={body}
                styles={styles}
                refs={sectionRefs}
                key={body && body.length ? body.map((block) => block.id).join(',') : 'default-key'}
              />

              <RelevantConcepts blocks={body} />
            </div>

            <div className={styles.postContentEmpty}></div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  )
}

export async function getServerSideProps(context) {
  try {
    const slug = context.params.slug

    const post = await getPost(slug)

    // If no post is found, return 404 status
    if (!post) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        post,
      },
    }
  } catch (error) {
    console.error('Error in getServerSideProps:', error)
    return {
      notFound: true,
    }
  }
}
